import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Container,
    Stack,
    Typography,
} from '@mui/material';
import { BadgeRounded, SwitchAccountRounded, VerifiedUserRounded } from '@mui/icons-material';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { EmployeeInvitationConfirmation, EmployeeInvitationState } from '../apiClients/EmployeePortalClient';
import { ApiRequestError } from '../apiClients/base/ApiBase';
import BusySpinner from '../components/BusySpinner';
import { useAccessInfo } from '../contexts/AccessTokenProvider';
import { SessionStorageItemIds } from '../Enums';

export const employeeInvitationMessage = (error: unknown): string => {
    if (!(error instanceof ApiRequestError))
        return 'We could not finish this invitation. Refresh this page, then retry.';
    switch (error.code) {
        case 'INVITATION_INVALID':
            return 'This invitation link is invalid. Ask your organization administrator to send a new one.';
        case 'INVITATION_EXPIRED':
            return 'This invitation has expired. Ask your organization administrator to send a new one.';
        case 'INVITATION_SESSION_EXPIRED':
            return 'Your secure confirmation session expired. Refresh this invitation page to start a new session.';
        case 'INVITATION_ALREADY_CLAIMED':
        case 'INVITATION_REPLAYED':
            return 'This invitation has already been used. Sign in with the account that claimed it, or ask an administrator for help.';
        case 'INVITATION_IDENTITY_CONFLICT':
            return 'This employment is already connected to another account. Switch accounts or ask an administrator to start the audited recovery process.';
        case 'INVITATION_REVOKED':
            return 'This invitation is no longer active. Ask your organization administrator for a new invitation.';
        default:
            return 'We could not finish this invitation. Refresh this page, then retry.';
    }
};

export const employeeInvitationStateMessage = (state: EmployeeInvitationState): string => {
    switch (state) {
        case 'Expired':
            return 'This invitation has expired. Ask your organization administrator to send a new one.';
        case 'Revoked':
            return 'This invitation is no longer active. Ask your organization administrator for a new invitation.';
        case 'Claimed':
        case 'Replayed':
            return 'This invitation has already been used. Sign in with the account that claimed it, or ask an administrator for help.';
        default:
            return '';
    }
};

const clearTerminalInvitation = (problem: unknown) => {
    if (problem instanceof ApiRequestError
        && ['INVITATION_INVALID', 'INVITATION_EXPIRED', 'INVITATION_ALREADY_CLAIMED', 'INVITATION_REPLAYED', 'INVITATION_REVOKED'].includes(problem.code))
        sessionStorage.removeItem(SessionStorageItemIds.EmployeeInvitationToken);
};

const EmployeeInvitationPage = () => {
    const location = useLocation();
    const history = useHistory();
    const {
        accessInfo,
        isLoggedIn,
        startEmployeeInvitation,
        prepareEmployeeInvitationSession,
        confirmEmployeeInvitation,
        switchEmployeeInvitationIdentity,
        reloadAfterEmployeeInvitationClaimed,
    } = useAccessInfo();
    const token = useMemo(
        () => new URLSearchParams(location.search).get('token')
            ?? sessionStorage.getItem(SessionStorageItemIds.EmployeeInvitationToken)
            ?? '',
        [location.search],
    );
    const validToken = token.length >= 24 && token.length <= 2048 && /^[A-Za-z0-9._~-]+$/.test(token);
    const loggedIn = isLoggedIn();
    const [confirmation, setConfirmation] = useState<EmployeeInvitationConfirmation | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useLayoutEffect(() => {
        if (validToken) {
            sessionStorage.setItem(SessionStorageItemIds.EmployeeInvitationToken, token);
            if (location.search)
                history.replace('/EmployeeInvitation');
        }
    }, [history, location.search, token, validToken]);

    useEffect(() => {
        if (!loggedIn || !validToken)
            return;
        const controller = new AbortController();
        setBusy(true);
        setError('');
        prepareEmployeeInvitationSession(token, controller.signal)
            .then(result => {
                if (result.State === 'ReadyForConfirmation') {
                    setConfirmation(result);
                    return;
                }
                setConfirmation(null);
                setError(employeeInvitationStateMessage(result.State));
                sessionStorage.removeItem(SessionStorageItemIds.EmployeeInvitationToken);
            })
            .catch(problem => {
                if (problem?.name !== 'AbortError') {
                    setError(employeeInvitationMessage(problem));
                    clearTerminalInvitation(problem);
                }
            })
            .finally(() => setBusy(false));
        return () => controller.abort();
    }, [loggedIn, prepareEmployeeInvitationSession, token, validToken]);

    const claim = async () => {
        setBusy(true);
        setError('');
        try {
            await confirmEmployeeInvitation();
            sessionStorage.removeItem(SessionStorageItemIds.EmployeeInvitationToken);
            const reloaded = await reloadAfterEmployeeInvitationClaimed();
            if (!reloaded)
                throw new Error('Portal access is not ready.');
            history.replace('/EmployeePortal');
        } catch (problem) {
            setConfirmation(null);
            setError(employeeInvitationMessage(problem));
            clearTerminalInvitation(problem);
        } finally {
            setBusy(false);
        }
    };

    if (!validToken)
        return <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 } }}><Alert severity="error">This employee portal invitation link is invalid.</Alert></Container>;

    return <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 7 }, px: { xs: 1.5, sm: 3 } }}>
        <Card variant="outlined">
            <BusySpinner busy={busy}>
                <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                    <Stack spacing={2.5} alignItems="stretch">
                        <Box sx={{ textAlign: 'center' }}>
                            <BadgeRounded color="primary" sx={{ fontSize: 52 }} />
                            <Typography variant="h5" component="h1" fontWeight={850}>Join your employee development portal</Typography>
                            {!loggedIn && <Typography color="text.secondary" sx={{ mt: 1 }}>
                                Sign in with an existing Synergist Pro account or create a verified account. The invitation email is only where the link was delivered; you choose the verified identity that receives this employment history.
                            </Typography>}
                        </Box>

                        {error && <Alert severity="warning">{error}</Alert>}

                        {!loggedIn ? <Alert icon={<VerifiedUserRounded />} severity="info">
                            Organization and employment details remain private until secure sign-in is complete.
                        </Alert> : confirmation && <>
                            <Alert severity="info">
                                You are signed in as <strong>{accessInfo.email}</strong>. Confirm only if this is the account you want to use for this employment relationship.
                            </Alert>
                            <Typography color="text.secondary">
                                Employer and employment details remain private until you explicitly confirm this identity and the invitation is successfully claimed.
                            </Typography>
                        </>}
                    </Stack>
                </CardContent>
                <CardActions sx={{ p: { xs: 2, sm: 3 }, pt: 0, flexDirection: { xs: 'column', sm: 'row' }, gap: 1, '& > :not(:first-of-type)': { ml: 0 }, '& .MuiButton-root': { minHeight: 48, width: { xs: '100%', sm: 'auto' } } }}>
                    {!loggedIn ? <Button variant="contained" onClick={() => startEmployeeInvitation(token)}>
                        Sign in or create a verified account
                    </Button> : <>
                        <Button variant="contained" disabled={!confirmation || busy} onClick={claim}>Confirm this identity</Button>
                        <Button startIcon={<SwitchAccountRounded />} disabled={busy} onClick={() => switchEmployeeInvitationIdentity(token)}>
                            Use another account
                        </Button>
                    </>}
                </CardActions>
            </BusySpinner>
        </Card>
    </Container>;
};

export default EmployeeInvitationPage;
